import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

// Modo oscuro/claro GLOBAL de la app. Alterna la clase `dark` en <html>; el CSS (index.css
// html.dark) redefine las variables de color, así toda la app se voltea sola. El botón vive en la
// cabecera (Layout). Componentes que calculan colores en JS (p.ej. la rejilla de /agenda) leen
// `dark` con useTheme().
//
// ── B-901 · DE FÁBRICA, CLARO; OSCURO SOLO SI ESA PERSONA LO HA ELEGIDO (27-sep-2026) ─────────────
// El dueño: «Por defecto, la app debe salir en modo claro, y solo sale en modo oscuro cuando ese usuario lo ha cambiado a
// modo oscuro ya que se debe guardar su preferencia». Antes, sin elección, seguía al sistema del móvil (un móvil en oscuro
// abría la app en oscuro sin que nadie lo pidiera), y la elección era del DISPOSITIVO, no de la persona: en el PC de
// recepción la decidía el último que pulsaba el botón para todos los que entraban después.
//
// AHORA LA ELECCIÓN ES DE LA PERSONA: se guarda en su perfil (`agentes.preferencias.tema_oscuro`, PATCH /auth/preferencias)
// y le sigue a cualquier dispositivo. En cada dispositivo queda además una copia POR PERSONA (`tema_oscuro:<id>`) para
// pintar el primer instante sin esperar al servidor. Sin sesión (la pantalla de entrada, el portal del paciente), claro.
// Quien aplica la de cada persona al entrar y al salir es components/TemaDeLaSesion.jsx: va dentro de la sesión, y este
// proveedor por fuera; por eso la sesión le REGISTRA cómo guardar en el perfil (`registrarGuardado`).
//
// LA ELECCIÓN DE ANTES (la clave `tema_oscuro` de este dispositivo, sin persona) se hereda una sola vez: si alguien había
// elegido oscuro aquí, lo conserva quien entre primero y se guarda en su perfil; después la clave se borra.
const ThemeContext = createContext({ dark: false, toggle: () => {}, aplicarUsuario: () => null, registrarGuardado: () => {} })

export const CLAVE_ANTIGUA = 'tema_oscuro'
export const claveDe = (id) => 'tema_oscuro:' + id
const leer = (k) => { try { return localStorage.getItem(k) } catch { return null } }
const escribir = (k, v) => { try { localStorage.setItem(k, v) } catch { /* sin almacenamiento: vale para esta sesión */ } }
const quitar = (k) => { try { localStorage.removeItem(k) } catch { /* nada */ } }

export function ThemeProvider({ children }) {
  const [dark, setDark] = useState(false)
  const darkRef = useRef(false)
  const usuario = useRef(null)
  const guardar = useRef(null)
  const poner = useCallback((v) => { darkRef.current = !!v; setDark(!!v) }, [])

  useEffect(() => { document.documentElement.classList.toggle('dark', dark) }, [dark])

  // La preferencia de quien tiene la sesión. `id` null = sin sesión → claro. `delServidor`: true/false si su perfil ya dice
  // algo (manda), undefined mientras no se sabe (vale su copia en este dispositivo, o claro). Devuelve true si ha heredado
  // la elección antigua de este dispositivo, para que quien llama (que tiene la sesión) la guarde en su perfil.
  const aplicarUsuario = useCallback((id, delServidor) => {
    usuario.current = id || null
    if (!id) { poner(false); return null }
    if (typeof delServidor === 'boolean') {
      escribir(claveDe(id), String(delServidor))
      quitar(CLAVE_ANTIGUA)
      poner(delServidor)
      return null
    }
    const suya = leer(claveDe(id))
    if (suya === 'true' || suya === 'false') { poner(suya === 'true'); return null }
    const antigua = leer(CLAVE_ANTIGUA)
    if (antigua === 'true') { escribir(claveDe(id), 'true'); quitar(CLAVE_ANTIGUA); poner(true); return true }
    if (antigua === 'false') quitar(CLAVE_ANTIGUA)
    poner(false)
    return null
  }, [poner])

  const registrarGuardado = useCallback((fn) => { guardar.current = typeof fn === 'function' ? fn : null }, [])

  // El botón: cambia el tema y, si hay sesión, lo apunta como SUYO (en este dispositivo y en su perfil). Sin sesión cambia
  // la pantalla y no se guarda en ningún sitio: no es de nadie.
  const toggle = useCallback(() => {
    const nuevo = !darkRef.current
    poner(nuevo)
    const id = usuario.current
    if (id) {
      escribir(claveDe(id), String(nuevo))
      if (guardar.current) guardar.current(nuevo)
    }
  }, [poner])

  return (
    <ThemeContext.Provider value={{ dark, toggle, aplicarUsuario, registrarGuardado }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => useContext(ThemeContext)
